// MentionsBadge — the unseen-mentions badge for the shell header (Epic 4 · S4.3).
//
// fn_my_mentions(p_unseen_only) + fn_mention_seen(p_id). Each mention:
//   { id, at, by, client, says, thread_id, task_id, task, seen }. Resolved at
// write time by a trigger. The badge shows the unseen count; clicking opens a
// list and clicking a mention marks it seen + opens the thread in the inbox
// drawer (not a new page).
//
// null count → no badge (couldn't load); 0 → no badge (nobody mentioned you —
// the normal state, not a thing to clutter the bar with); >0 → red badge.
// Conflating null and zero is a lie (00_CONTRACT §6).

import React, { useEffect, useState } from 'react';
import { useMentions } from '../../../hooks/useMentions';
import { useShellSurface } from '../../../contexts/ShellSurfaceContext';
import { formatWhen } from '../../shared/format';

export default function MentionsBadge() {
  const { unseen, mentions, markSeen } = useMentions();
  const { openInbox } = useShellSurface();
  const [open, setOpen] = useState(false);

  // Close the popover on Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const onPick = async (m) => {
    await markSeen(m.id);
    setOpen(false);
    openInbox(m.thread_id ?? m.threadId ?? null);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
        aria-label={unseen > 0 ? `${unseen} unread mentions` : 'Mentions'}
        title="Mentions"
        data-testid="hub-mentions-btn"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        {unseen != null && unseen > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white tabular-nums"
            data-testid="hub-mentions-count"
          >
            {unseen > 99 ? '99+' : unseen}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-10 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900"
          data-testid="mentions-popover"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-800">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Mentions</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Close
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {mentions.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No unread mentions.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="mentions-list">
                {mentions.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => onPick(m)}
                      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                            {m.by || m.author || 'Someone'}
                          </span>
                          {m.client && (
                            <span className="ml-auto shrink-0 text-xs text-gray-400">{m.client}</span>
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-sm text-gray-600 dark:text-gray-300">
                          {m.says || m.text || (m.task ? `mentioned you on ${m.task}` : 'mentioned you')}
                        </span>
                        {m.at && (
                          <span className="mt-0.5 block text-xs text-gray-400 tabular-nums">{formatWhen(m.at)}</span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
